import { Injectable } from '@angular/core';
import {
  DocumentAdditionalDataValidity,
  SelectedSignatoryDetails,
  SignatoryDetails,
  SignatoryWithSigningStatus,
  SignUploadDocsInput
} from 'appRootPath/models/commercial-papers.model';
import {
  DEAL_CONFIRMATION_NOTE,
  DocumentObject,
  LETTER_OF_OFFER,
  MODAL_OPTIONS,
  PROMISSORY_NOTE,
  UI_DATE_FORMAT_MAP
} from 'appRootPath/shared/global-meta-data';
import { Subject } from 'rxjs';
import * as moment from 'moment';
import { getCalendarDateObj } from 'appRootPath/utils';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  UploadSignedDocsConfirmComponent
} from 'appRootPath/shared/upload-signed-documents/upload-signed-docs-confirm/upload-signed-docs-confirm.component';
import { COMMON_MESSAGES } from 'appRootPath/messages';
import { ToastrService } from 'ngx-toastr';
import { CpMarketplaceApiService } from './cp-marketplace-api.service';


const docsToSkip = [LETTER_OF_OFFER, PROMISSORY_NOTE];

@Injectable({
  providedIn: 'root'
})
export class CpDocumentationActionsService {

  signUploadPanelSubject = new Subject<SignUploadDocsInput | null>();
  signUploadPanelObservable = this.signUploadPanelSubject.asObservable();
  constructor(
    private modalService: NgbModal,
    private toastr: ToastrService,
    private cpMarketAPIService: CpMarketplaceApiService
  ) { }

  /**
   * Show the sign upload documents panel for issuer documentation
   * @param signUploadPanelInput the inputs for sign upload docs panel
   */
  showSignUploadPanel(signUploadPanelInput: SignUploadDocsInput) {
    this.signUploadPanelSubject.next(signUploadPanelInput);
  }

  /**
   * check if doc date is before loo date. 
   * All Document dates must be less than or equal to Letter Of Offer date
   * @param documents the existing additional data for all documents
   */
  getDocsAdditionalDateValidity(documents, issueValueDate) {
    const looDocData: DocumentObject = documents.find(doc => doc.document_name === LETTER_OF_OFFER);
    const looDate = (looDocData && looDocData.additional_data &&
      looDocData.additional_data.document_date) || null;
    const docsDateValidityMap = {};

    documents.forEach((doc: DocumentObject) => {
      docsDateValidityMap[doc.document_name] = docsToSkip.includes(doc.document_name) ?
        this.createValidityMapForDoc(doc, null, issueValueDate) :
        this.createValidityMapForDoc(doc, looDate, issueValueDate);
    });

    return docsDateValidityMap;
  }

  /**
   * Using looDate and doc date, returns if doc date is before loo date,
   * whether to show validation message and looDate
   * @param doc document data
   * @param looDate letter of offer Date
   * @returns if doc date is before loo date, whether to show validation message and looDate
   */
  createValidityMapForDoc(doc: DocumentObject, looDate: string | null, issueValueDate: string):
    DocumentAdditionalDataValidity {
    let isBeforeLoo = false;
    let isMessageToBeShown = false;
    const docDate = doc.additional_data && doc.additional_data.document_date;

    if (!docsToSkip.includes(doc.document_name) && docDate && looDate) {
      isMessageToBeShown = true;
      isBeforeLoo = moment(docDate).isBefore(moment(looDate));
    }

    return {
      looDate,
      isMessageToBeShown,
      isBeforeLoo,
      issueValueDate
    };
  }

  /**
   * initialize dates for minimum, maximum and validation displays.
   */
  initDatesForValidation(docDateValidityMap: DocumentAdditionalDataValidity) {
    const looDateFormatted = docDateValidityMap.looDate ?
      moment(docDateValidityMap.looDate).format(UI_DATE_FORMAT_MAP.default) :
      '';
    const looDateObj = docDateValidityMap.looDate ?
      getCalendarDateObj(moment(docDateValidityMap.looDate).toDate()) :
      null;

    const issueValueDateFormatted =
      moment(docDateValidityMap.issueValueDate).format(UI_DATE_FORMAT_MAP.default);
    const issueValueDateObj = getCalendarDateObj(moment(docDateValidityMap.issueValueDate).toDate());

    return [
      looDateFormatted,
      looDateObj,
      issueValueDateFormatted,
      issueValueDateObj
    ];
  }

  /**
   * For the given signatories selected in the doc and authorised signatories,
   * returns the list of their details along with signing status.
   * @param docSignatories - List of signatories selected in the document for signing.
   * @param authSignatoriesList - List of authorised signatories of the company.
   */
  parseFoundSignatories(docSignatories: SelectedSignatoryDetails[], authSignatoriesList: SignatoryDetails[]) {
    const existingSelectedSignatories: SignatoryWithSigningStatus[] = [];

    authSignatoriesList.forEach(signatory => {
      const correspondingDocSignatory =
        docSignatories.filter(docSign => Boolean(docSign.email === signatory.email));
      if (correspondingDocSignatory.length === 1) {
        existingSelectedSignatories.push({
          ...signatory,
          signed: correspondingDocSignatory[0].signed,
          is_email_sent: correspondingDocSignatory[0].is_email_sent,
          ...correspondingDocSignatory[0]
        });
      }
    });

    return existingSelectedSignatories;
  }

  /**
 * flattens doc data
 */
  flattenAllDocuments(documents) {
    const flattenedDocs = [];
    documents.forEach(doc => {
      if (doc.document_name !== DEAL_CONFIRMATION_NOTE) {
        flattenedDocs.push({ ...doc });
      } else {
        flattenedDocs.push(...doc.dcnDocs);
      }
    });

    return flattenedDocs;
  }

  /**
 * API call to remind signatories by re-sending an email notification to sign the docs
 */
  remindSignatories(issueId: string, documents: DocumentObject[],
    authorisedSignatories, isFlattened = false) {
    const docSignatoryData = [];
    const flattenedDocs = isFlattened ? documents : this.flattenAllDocuments(documents);
    flattenedDocs.forEach((doc) => {
      // doc signatory list doesnt have group name, designation and other data
      // find in auth signatory list and append to selectedSignatories data
      // only send reminder to signatories who have received email and have not signed yet
      const selectedSignatories = this.parseFoundSignatories(
        doc.signatories_list.filter(sig => sig.is_email_sent && !sig.signed),
        authorisedSignatories);

      // if there are signatories to remind add it to docSignatoryData for modal window.
      if (selectedSignatories.length > 0) {
        docSignatoryData.push({
          document_name: doc.document_name,
          selectedSignatories
        });
      }
    });

    this.getUserConfirmation(docSignatoryData).result.then((isAcceptedByUser) => {
      if (isAcceptedByUser) {
        // tslint:disable-next-line: deprecation
        this.cpMarketAPIService.resendEmailToSignatories(issueId).subscribe((resendEmailResp: any) => {
          if (resendEmailResp) {
            this.toastr.success(COMMON_MESSAGES.RESEND_EMAIL_SUCCESS);
          }
        });
      }
    });
  }

  /**
   * Show confirmation modal with signatory/document data and return user confirmation modal data
   * @param documentsToBeSend tentative documents to be sent for signing/uploaded
   */
  getUserConfirmation(documentsToBeSend): NgbModalRef {
    const modalRef = this.modalService.open(UploadSignedDocsConfirmComponent, MODAL_OPTIONS);
    modalRef.componentInstance.uploadSignData = documentsToBeSend;
    modalRef.componentInstance.modalTitle = COMMON_MESSAGES.REMIND_CONFIRM_TITLE;
    modalRef.componentInstance.modalConfirmMsg = COMMON_MESSAGES.SEND_REMINDER_TO_SIGNATORY;

    return modalRef;
  }

}
